import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requireSupabase, supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { useAuth } from '@/features/auth/useAuth';

/**
 * Notifications.
 *
 * Elles vivaient dans `features/messaging` — un héritage de l'époque où seule
 * la messagerie en émettait. Elles concernent désormais les réservations, les
 * messages et les annonces : elles méritent leur propre domaine.
 */

export type Notification = {
  id: string;
  user_id: string;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  payload: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
};

export function useNotifications(limit = 30) {
  return useQuery({
    queryKey: ['notifications', 'list', limit],
    enabled: isSupabaseConfigured,
    queryFn: async () => {
      const { data, error } = await requireSupabase()
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as Notification[];
    },
  });
}

/**
 * Le compteur de la cloche.
 *
 * Compté par le serveur : la cloche s'actualise souvent, et rapatrier trente
 * lignes pour n'en garder que le nombre serait du gâchis à chaque battement.
 */
export function useUnreadCount() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['notifications', 'unread'],
    enabled: isSupabaseConfigured && isAuthenticated,
    // Filet de sécurité si le temps réel n'est pas actif sur le projet.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await requireSupabase().rpc('unread_notifications_count');
      if (error) throw new Error(error.message);
      return (data as number) ?? 0;
    },
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await requireSupabase()
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await requireSupabase().rpc('mark_all_notifications_read');
      if (error) throw new Error(error.message);
      return (data as number) ?? 0;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

/** Supprime une notification. Elles n'appartiennent qu'à leur destinataire. */
export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await requireSupabase().from('notifications').delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

/**
 * Vide les notifications déjà lues.
 *
 * Seulement les lues : effacer une notification jamais vue, c'est effacer
 * l'information avant qu'elle ait servi. La règle est appliquée par le serveur,
 * pas par ce bouton.
 */
export function useClearReadNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await requireSupabase().rpc('delete_read_notifications');
      if (error) throw new Error(error.message);
      return (data as number) ?? 0;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

/**
 * Allume la cloche à la seconde où la notification est écrite.
 *
 * Deux précautions, toutes deux apprises en exécutant ce code :
 *
 * 1. Le NOM du canal est unique à chaque montage. `supabase.channel(nom)` rend
 *    le canal EXISTANT si ce nom est déjà pris ; appeler `.on()` sur un canal
 *    déjà abonné lève « cannot add postgres_changes callbacks after
 *    subscribe() ». Cette exception faisait tomber l'application entière :
 *    page blanche, plus d'en-tête, plus rien.
 *
 * 2. Tout est sous `try`. Le temps réel est un confort — il fait apparaître
 *    une pastille plus vite. Il ne doit JAMAIS pouvoir empêcher quelqu'un
 *    d'utiliser le site. S'il échoue, le sondage d'une minute prend le relais
 *    et personne ne remarque rien.
 */
export function useNotificationsRealtime() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  useEffect(() => {
    const client = supabase;
    const userId = profile?.id;
    if (!client || !userId) return;

    const topic = `notifications:${userId}:${Math.random().toString(36).slice(2, 10)}`;
    let channel: ReturnType<typeof client.channel> | null = null;

    try {
      channel = client
        .channel(topic)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
          () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
        )
        .subscribe();
    } catch {
      channel = null;
    }

    return () => {
      if (!channel) return;
      try {
        void client.removeChannel(channel);
      } catch {
        // Le canal est deja parti : rien a faire.
      }
    };
  }, [profile?.id, queryClient]);
}
